import React, { useEffect, useState } from 'react';
import { withCookies, Cookies, ReactCookieProps } from 'react-cookie';
import { Helmet } from 'react-helmet-async';
import { shallowEqual, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Grid } from 'elements';
import { RootState } from 'modules';
import PopUpDC from 'PopUpDC';
import ExplainDetail, { TDetailType } from 'components/case/parts/Detail';
import Information from 'components/case/parts/Information';
import { showAlert } from 'components/modal/showConfirm';
import SMSVerificationModal from 'components/user/SMSVerificationModal';
import {
  getCaseItem,
  getCaseExplainDetail,
  getCaseMenteeExplainDetail,
  delCaseMenteeExplainDetail,
} from 'libs/api/case';
import { TCase, TCaseExplainDetail } from 'modules/case/types';
import { TModalState } from 'modules/types';
import { Spin } from 'antd';
import { Colors } from 'GlobalStyles';

interface ICookieProps extends ReactCookieProps {}

const CaseDetail = (props: ICookieProps) => {
  const ignoreSMSVerificationUser = [
    'test@naver.com',
    'llilyha123@naver.com',
    'nhj2308@gmail.com',
    'dongjunk@umich.edu',
  ]; //번호인증안되는 특정유저 관련하여 문자인증 없이 사용할 수 있도록 하드코딩
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const history = useHistory();
  const {
    id,
    detail_type,
    detail_id,
  }: {
    id: string;
    detail_type: keyof TDetailType;
    detail_id: string;
  } = useParams();
  const [cs, setCs] = useState<TCase>();
  const [detail, setDetail] = useState<TCaseExplainDetail>();
  const [smsVerificationModal, setSMSVerificationModal] = useState<TModalState>({
    record: { type: 'phone' },
    visible: true,
    onFinish: (success: boolean) => {
      if (success) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    },
  });

  //prop.cookies를 통해 쿠키에 접근
  const [cookies, setCookies] = useState<Cookies | undefined>(props.cookies);
  //쿠키를 가지고 있지 않아야 (isAuthenticated === false) ==>  모달 열림!!!!
  const [isAuthenticated, setIsAuthenticated] = useState(
    detail_type === 'mentee' || (currentUser && ignoreSMSVerificationUser.includes(currentUser?.userid)),
  );
  //인증만료 시간
  const [authenticationExpiredTime, setAuthenticationExpiredTime] = useState<Date>();

  const getCase = async () => {
    const { success, data } = await getCaseItem(id);
    if (success) setCs(data.result);
  };

  const getDetail = async () => {
    if (detail_type === 'mentee') {
      const { success, data } = await getCaseMenteeExplainDetail(detail_id);
      if (success) {
        const detail: TCaseExplainDetail = {
          id: data.id,
          user_rating: data.user_rating,
          mentor_ratings: data.mentee_ratings,
          rating_count: data.rating_count,
          comments_count: data.comments_count,
          title: data.title,
          content: data.content,
          mentor: {
            id: data.user.id,
            name: data.mentee_name,
            profile_image: data.mentee_profile_image,
            university: data.mentee_university,
            major: data.mentee_major,
          },
          question: data.question,
          key_question: data.key_question,
          created_at: data.created_at,
          hit_count: data.hit_count,
          user_id: data.user_id,
          user_phone_number: data.user_phone_number,
        };
        setDetail(detail);
      }
    } else {
      const { success, data } = await getCaseExplainDetail(detail_id);
      if (success) setDetail(data);
    }
  };

  //인증 만료시간 관련
  useEffect(() => {
    if (detail_type !== 'mentee') {
      // 쿠키가 있다?
      if (cookies) {
        //-> 관련 키값이 있다?
        const authenticationCookies = cookies.get(PopUpDC.CASE_NOTE_AUTHENTICATION_COOKIE_VALUE);
        if (authenticationCookies) {
          //authenticationCookies 값이 있으면 인증 요청 모달이 뜨면안됨 ==> setIsAuthenticated(true);
          setIsAuthenticated(!!authenticationCookies);
        }
        if (!authenticationCookies && isAuthenticated) {
          const expires = authenticationExpiredTime;
          expires &&
            cookies.set(PopUpDC.CASE_NOTE_AUTHENTICATION_COOKIE_VALUE, true, {
              path: `/`,
              expires,
            });
        }
      } else {
        setCookies(props.cookies);
      }
    }
  }, [authenticationExpiredTime]);

  useEffect(() => {
    if (!smsVerificationModal.visible && !isAuthenticated) {
      showAlert(() => history.go(-1), '상세 설명을 확인하기 위해서는 핸드폰 인증이 필요합니다.');
    }
  }, [smsVerificationModal]);

  useEffect(() => {
    if (isAuthenticated) {
      getCase();
      getDetail();
    }
  }, [isAuthenticated, authenticationExpiredTime, props.cookies, cookies]);

  if (!isAuthenticated) {
    return (
      <SMSVerificationModal
        type={'case'}
        modalState={smsVerificationModal}
        setModalState={setSMSVerificationModal}
        setExpiredTime={setAuthenticationExpiredTime}
      />
    );
  }

  if (!cs) {
    return (
      <Grid justify='center' align='center' height='100px' margin='0 0 150px 0' md={{ margin: '0 0 80px 0' }}>
        <Spin spinning />
      </Grid>
    );
  }

  return (
    <Grid justify='center' margin='0 0 150px 0' md={{ margin: '0 0 80px 0' }}>
      <Helmet>
        {/* <title>시즌별 케이스 노트 - 2021년 하반기 Case</title> */}
        <title>케이스 노트</title>
      </Helmet>
      <Information item={cs} refresh={getCase} />
      <Grid style={{ borderTop: `1px solid ${Colors.grey1}` }} />
      {!!detail && (
        <ExplainDetail
          id={id}
          detail_id={detail_id}
          type={detail_type}
          detail={detail}
          del={detail_type === 'mentee' ? delCaseMenteeExplainDetail : undefined}
          refresh={getDetail}
        />
      )}
    </Grid>
  );
};

export default withCookies(CaseDetail);
